import { ApolloClient, ApolloQueryResult, gql, NormalizedCacheObject } from '@apollo/client'
import { Operation } from '@arcxgame/contracts/dist/arc-types/core'

export interface Quest {
  name: string
  description: string
  isCompleted: boolean
  xp: number
}

export interface QuestsSkillSet {
  name: string
  value: number
  icon: string
  quests: Quest[]
}

export class ProductQuestsService {
  GET_USER_SLASHED = gql`
    query UserSlashed($slasher: Bytes!) {
      userSlasheds(where: { slasher: $slasher }) {
        id
        slasher
        user
      }
    }
  `

  GET_ACTION_OPERATED = gql`
    query ActionOperated($operation: Int!, $sender: Bytes!) {
      actionOperateds(where: { operation: $operation, sender: $sender }) {
        id
        amountOne
        amountTwo
        operation
        sender
      }
    }
  `

  constructor(
    private appolloClient: ApolloClient<NormalizedCacheObject>,
    private userAddress: string,
  ) {}

  async isLinkUsdMinted() {
    const result = await this.getActionOperateds(Operation.Open)
    return !this.isEntityForDataEmpty(result, 'actionOperateds')
  }

  async isLinkUsdLiquidated() {
    const result = await this.getActionOperateds(Operation.Liquidate)
    return !this.isEntityForDataEmpty(result, 'actionOperateds')
  }

  async isSlashedAnotherUserInRewardPool() {
    const result = await this.getUserSlashedEvents()
    return !this.isEntityForDataEmpty(result, 'userSlasheds')
  }

  async getProductQuestsSkillSet(): Promise<QuestsSkillSet> {
    const quests = await this.getQuests()
    return {
      name: 'Product Quests',
      value: await this.getValue(quests),
      icon: 'tools',
      quests,
    }
  }

  async getQuests(): Promise<Quest[]> {
    return [
      {
        name: 'Mint LINKUSD Debt',
        description: 'Open at least one LINKUSD vault',
        isCompleted: await this.isLinkUsdMinted(),
        xp: 1,
      },
      {
        name: 'Execute liquidation',
        description: 'Liquidate an undercollateralised vault',
        isCompleted: await this.isLinkUsdLiquidated(),
        xp: 2,
      },
      {
        name: 'Slashing',
        description: 'Slash another user in a reward pool',
        isCompleted: await this.isSlashedAnotherUserInRewardPool(),
        xp: 1,
      },
    ]
  }

  async getValue(quests: Quest[]) {
    return quests
      .filter((quest) => quest.isCompleted)
      .reduce((prev, current) => prev + current.xp, 0)
  }

  private async getUserSlashedEvents() {
    return this.appolloClient.query({
      query: this.GET_USER_SLASHED,
      variables: { slasher: this.userAddress },
    })
  }

  private getActionOperateds(operation: Operation) {
    return this.appolloClient.query({
      query: this.GET_ACTION_OPERATED,
      variables: { operation, sender: this.userAddress },
    })
  }

  private isEntityForDataEmpty(result: ApolloQueryResult<unknown>, entityName: string) {
    if (!result.data) {
      throw Error('Data not found')
    }
    return result.data[entityName].length === 0
  }
}
